import { create } from "zustand"

import type {
  CompaniesCertificationsQuery,
  CompanyListCertification,
  CompanyListCertificationSortField,
  SortingOrder,
} from "@/graphql/codegen/graphql"
import { CompaniesCertificationsDocument } from "@/graphql/codegen/graphql"
import { type SearchFilters, createSearchStore } from "@/stores/createSearchStore"

export type CertificationExpirationsSearchFilters = SearchFilters & {
  category?: string
  field?: CompanyListCertificationSortField
  order?: SortingOrder
}

const deduplicateLocations = (
  newData: CompanyListCertification[],
  existingData: CompanyListCertification[]
): CompanyListCertification[] => {
  const uniqueIds = new Set(existingData.map((location) => location.companyId))
  return [...existingData, ...newData.filter((location) => !uniqueIds.has(location.companyId))]
}

export const useCertificationExpirationsSearchStore = create(
  createSearchStore<CompanyListCertification, CertificationExpirationsSearchFilters, CompaniesCertificationsQuery>({
    documentNode: CompaniesCertificationsDocument,
    deduplicateData: deduplicateLocations,
    getQueryInput: (filters, offset, limit) => {
      const { category, field, order, ...otherFilters } = filters
      return {
        filter: otherFilters,
        offset,
        limit,
        sort: { field, order },
      }
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: data?.company?.listCertifications?.__typename ?? "CompanyListCertificationFailure",
      data:
        data?.company?.listCertifications?.__typename === "CompanyListCertificationSuccess"
          ? (data.company.listCertifications.companies as CompanyListCertification[])
          : [],
      error:
        data?.company?.listCertifications?.__typename === "CompanyListCertificationFailure"
          ? data.company.listCertifications?.error
          : undefined,
    }),
  })
)
